import { type ActionReducerMapBuilder, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import { isCanceledError } from 'src/helper/api';
import { parseApiError } from 'src/helper/utils';
import {
  getEcfEventDetails,
  getEcfEventLifecycle,
  getEcfEvents,
  getEcfLumiConfig,
  getEcfOverview,
  getEcfStatus,
  getEcfYardiMappings,
} from './api';
import {
  type EcfEventDetail,
  type EcfEventsResponse,
  type EcfOverview,
  type EcfOverviewResponse,
  type EcfStatus,
  type EcfWorkflowOverview,
  type ExternalConnectionsState,
  initialState,
  type LumiBranding,
  type LumiBrandingImage,
  type LumiBrandingSlot,
  type LumiConfig,
  type LumiConfigAreaKey,
  type LumiTimeSlot,
  type YardiMappingsConfig,
  type YardiMappingsConfigResponse,
} from './types';

const EMPTY_WORKFLOW_OVERVIEW: EcfWorkflowOverview = Object.freeze({
  total: 0,
  resolved: 0,
  active: 0,
  needs_review: 0,
  options: [],
  active_details: { count: 0, recent: [] },
  needs_review_details: { count: 0, recent: [] },
});

const normalizeEcfOverview = (overview: EcfOverviewResponse): EcfOverview => ({
  ...overview,
  workflows: overview.workflows ?? EMPTY_WORKFLOW_OVERVIEW,
});

/** Extracts a human-readable error, returning null for cancelled requests. */
function toError(payload: unknown): string | null {
  if (isCanceledError(payload)) return null;
  return parseApiError(payload);
}

const optionKeys = (options: unknown): string[] =>
  (Array.isArray(options) ? options : []).flatMap((option: unknown) => {
    if (typeof option === 'string') return [option];
    if (!option || typeof option !== 'object') return [];
    const key = (option as { key?: unknown }).key;
    return typeof key === 'string' ? [key] : [];
  });

const DAY_ALIASES: Record<string, string> = {
  mon: 'monday',
  tue: 'tuesday',
  wed: 'wednesday',
  thu: 'thursday',
  fri: 'friday',
  sat: 'saturday',
  sun: 'sunday',
};

const normalizeAvailableDays = (days: unknown): string[] =>
  Array.from(
    new Set(
      (Array.isArray(days) ? days : [])
        .filter((day): day is string => typeof day === 'string')
        .map((day) => day.trim().toLowerCase())
        .map((day) => DAY_ALIASES[day] ?? day),
    ),
  );

/**
 * Older LuMi tenant rows stored only start/end. Give those windows deterministic
 * keys and editable labels so an administrator can repair and save them through
 * the current complete contract.
 */
const normalizeTimeSlots = (slots: unknown): LumiTimeSlot[] =>
  (Array.isArray(slots) ? slots : [])
    .filter((slot): slot is Record<string, unknown> => !!slot && typeof slot === 'object')
    .map((slot, index) => {
      const start = typeof slot.start === 'string' ? slot.start : '';
      const end = typeof slot.end === 'string' ? slot.end : '';
      const rawKey = typeof slot.key === 'string' ? slot.key.trim() : '';
      const rawLabel = typeof slot.label === 'string' ? slot.label.trim() : '';
      const generatedKey = `window-${start.replace(':', '') || 'start'}-${
        end.replace(':', '') || 'end'
      }-${index + 1}`;

      return {
        key: rawKey || generatedKey,
        label: rawLabel || `Time window ${index + 1}`,
        start,
        end,
        ...(typeof slot.emergency_enabled === 'boolean'
          ? { emergency_enabled: slot.emergency_enabled }
          : {}),
      };
    });

/**
 * The url goes straight into `<img src>`, so only an absolute `https:` URL is kept. Anything else —
 * `javascript:`, `data:`, `http:`, protocol-relative, unparseable — is a malformed url: null.
 */
const toSafeImageUrl = (url: unknown): string | null => {
  if (typeof url !== 'string' || url === '') return null;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
};

/**
 * `null` = no image; `undefined` = malformed. A missing hash is malformed; a missing or unsafe url
 * is not (the image stays restorable and clearable, just without a preview).
 */
const toBrandingImage = (raw: unknown): LumiBrandingImage | null | undefined => {
  if (raw === null) return null;
  if (!raw || typeof raw !== 'object') return undefined;
  const { hash, url } = raw as { hash?: unknown; url?: unknown };
  if (typeof hash !== 'string' || hash === '') return undefined;
  return { hash, url: toSafeImageUrl(url) };
};

const toBrandingSlot = (raw: unknown): LumiBrandingSlot | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const slot = raw as { current?: unknown; previous?: unknown; can_roll_back?: unknown };
  // A MISSING `current` is malformed (undefined), not empty — only an explicit null means "no
  // image". `previous` only feeds Restore, so an unusable one just means nothing to restore: the
  // slot stays usable rather than hiding the whole section.
  const current = toBrandingImage(slot.current);
  if (current === undefined) return undefined;
  const previous = toBrandingImage(slot.previous) ?? null;
  return { current, previous, can_roll_back: slot.can_roll_back === true && previous !== null };
};

/**
 * Narrows LuMi's branding Show shape. Absent or malformed → undefined (= unavailable). NEVER `{}`:
 * an empty object would render as "no logo set", telling an Admin the images are gone when the
 * backend simply did not send them.
 */
export function normalizeLumiBranding(raw: unknown): LumiBranding | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const logo = toBrandingSlot((raw as { logo?: unknown }).logo);
  const favicon = toBrandingSlot((raw as { favicon?: unknown }).favicon);
  return logo && favicon ? { logo, favicon } : undefined;
}

/**
 * LuMi's access GET enriches enabled options with labels and descriptions while its PUT accepts
 * the option keys. Keep that serialization detail out of the forms so a GET can be saved safely.
 *
 * `branding` is stripped and re-added only when valid, so the key is either a complete
 * {@link LumiBranding} or absent — never `undefined` or `{}`.
 */
function normalizeLumiConfig(input: LumiConfig): LumiConfig {
  const { branding: rawBranding, ...rest } = input;
  const branding = normalizeLumiBranding(rawBranding);
  return {
    ...rest,
    availability: input.availability
      ? {
          ...input.availability,
          available_days: normalizeAvailableDays(input.availability.available_days),
          time_slots: normalizeTimeSlots(input.availability.time_slots),
        }
      : input.availability,
    access: input.access
      ? {
          ...input.access,
          enabled_entry_types: optionKeys(input.access.enabled_entry_types),
          enabled_pet_types: optionKeys(input.access.enabled_pet_types),
        }
      : input.access,
    ...(branding ? { branding } : {}),
  };
}

/**
 * Applies a fresh lumi-config read to the stored config, keeping the reference of every key whose
 * value did not change. Each area form resets its draft when its `value` changes, so a re-read that
 * only repeats what is stored (the one after a branding write, Refresh) leaves an Admin's unsaved
 * edits in the other sections alone; an area that changed on the server still resets. Keys the
 * read no longer carries (`_errors`, `branding`) are removed.
 *
 * `keep` lists areas saved while the read was in flight: its value for them predates the save, so
 * they are neither replaced nor removed.
 */
function applyLumiConfigRead(
  stored: LumiConfig,
  next: LumiConfig,
  keep: readonly LumiConfigAreaKey[] = [],
): void {
  const target = stored as unknown as Record<string, unknown>;
  const fresh = next as unknown as Record<string, unknown>;
  const kept = new Set<string>(keep);
  for (const key of Object.keys(target)) {
    if (!kept.has(key) && !(key in fresh)) delete target[key];
  }
  for (const [key, value] of Object.entries(fresh)) {
    if (!kept.has(key) && !isEqual(target[key], value)) target[key] = value;
  }
}

/**
 * Drops a read's `_errors` entries for the given areas: they predate a save that succeeded since,
 * so they would mark a just-saved area unavailable. Removes `_errors` once nothing is left in it.
 */
function withoutAreaErrors(next: LumiConfig, areas: readonly LumiConfigAreaKey[]): LumiConfig {
  if (!next._errors || areas.length === 0) return next;
  const { _errors, ...rest } = next;
  const errors = Object.fromEntries(
    Object.entries(_errors).filter(([key]) => !(areas as readonly string[]).includes(key)),
  );
  return Object.keys(errors).length > 0 ? { ...rest, _errors: errors } : rest;
}

const emptyPriorityMappings = () => ({
  inbound_mapping: {},
  outbound_mapping: { urgent: null, high: null, medium: null, low: null },
  emergency_priorities: [],
});

/** Keeps a frontend-first priority rollout safe against the previous GET shape. */
function normalizeYardiMappings(input: YardiMappingsConfigResponse): YardiMappingsConfig {
  return {
    ...input,
    catalog: {
      ...input.catalog,
      priorities: Array.isArray(input.catalog.priorities) ? input.catalog.priorities : [],
    },
    readiness: {
      ...input.readiness,
      priority_inbound_total: input.readiness.priority_inbound_total ?? 0,
      priority_inbound_mapped: input.readiness.priority_inbound_mapped ?? 0,
      priority_outbound_total: input.readiness.priority_outbound_total ?? 0,
      priority_outbound_mapped: input.readiness.priority_outbound_mapped ?? 0,
    },
    priority: input.priority ?? emptyPriorityMappings(),
  };
}

const externalConnectionsSlice = createSlice({
  name: 'externalConnections',
  initialState,
  reducers: {
    clearEventDetail: (state: ExternalConnectionsState) => {
      state.eventDetail = null;
      state.eventDetailError = null;
      state.eventDetailLoading = false;
      // The lifecycle belongs to the closed drawer — dropping it here stops a reopen on a
      // DIFFERENT entity from briefly rendering the previous entity's history.
      state.lifecycle = [];
      state.lifecycleError = null;
      state.lifecycleLoading = false;
      state.lifecycleWorkOrderUuid = null;
      state.lifecycleTotal = 0;
    },
    /**
     * Records which work order the lifecycle is being loaded for. Dispatched immediately before
     * the fetch so that stepping through events on the SAME entity (clicking a timeline row)
     * reuses the loaded history instead of refetching it on every detail swap.
     */
    setLifecycleTarget: (state: ExternalConnectionsState, action: PayloadAction<string | null>) => {
      state.lifecycleWorkOrderUuid = action.payload;
    },
    /**
     * Keeps the slice the source of truth after a successful per-area PUT. A save that lands while
     * a read is in flight is recorded, so that read (which started before it) cannot revert it.
     */
    setLumiConfigArea: (
      state: ExternalConnectionsState,
      action: PayloadAction<{ key: LumiConfigAreaKey; value: LumiConfig[LumiConfigAreaKey] }>,
    ) => {
      if (!state.lumiConfig) return;
      const { key, value } = action.payload;
      state.lumiConfig = { ...state.lumiConfig, [key]: value };
      // `lumiConfigLoading` is the in-flight signal: the request id outlives the read it names.
      if (state.lumiConfigLoading) {
        const saved = state.lumiConfigAreasSavedDuringRead ?? [];
        state.lumiConfigAreasSavedDuringRead = saved.includes(key) ? saved : [...saved, key];
      }
    },
    /**
     * Stores the authoritative branding a write returned. Touches ONLY `branding`: the other keys
     * keep their references, which the Resident Portal drafts rely on to survive (see
     * useLumiAreaDraft). An invalid payload is ignored rather than turning "unavailable" into "empty".
     */
    setLumiBranding: (state: ExternalConnectionsState, action: PayloadAction<LumiBranding>) => {
      if (!state.lumiConfig) return;
      const branding = normalizeLumiBranding(action.payload);
      if (!branding) return;
      state.lumiConfig = { ...state.lumiConfig, branding };
    },
    setYardiMappings: (
      state: ExternalConnectionsState,
      action: PayloadAction<YardiMappingsConfigResponse>,
    ) => {
      state.yardiMappings = normalizeYardiMappings(action.payload);
      state.yardiMappingsError = null;
    },
    clearExternalConnections: () => initialState,
  },
  extraReducers: (builder: ActionReducerMapBuilder<ExternalConnectionsState>) => {
    // ----- STATUS -----
    builder.addCase(getEcfStatus().pending, (state) => {
      state.statusLoading = true;
      state.statusError = null;
    });
    builder.addCase(getEcfStatus().fulfilled, (state, action) => {
      state.status = action.payload as EcfStatus;
      state.statusLoading = false;
    });
    builder.addCase(getEcfStatus().rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return; // cancelled — leave loading as-is for the retry
      state.statusError = message;
      state.statusLoading = false;
    });

    // ----- OVERVIEW -----
    builder.addCase(getEcfOverview().pending, (state) => {
      state.overviewLoading = true;
      state.overviewError = null;
    });
    builder.addCase(getEcfOverview().fulfilled, (state, action) => {
      state.overview = normalizeEcfOverview(action.payload as EcfOverviewResponse);
      state.overviewLoading = false;
    });
    builder.addCase(getEcfOverview().rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.overviewError = message;
      state.overviewLoading = false;
    });

    // ----- EVENT LEDGER -----
    builder.addCase(getEcfEvents().pending, (state) => {
      state.eventsLoading = true;
      state.eventsError = null;
    });
    builder.addCase(getEcfEvents().fulfilled, (state, action) => {
      const { data, meta } = action.payload as EcfEventsResponse;
      state.events = data ?? [];
      state.eventsMeta = meta ?? null;
      state.eventsLoading = false;
    });
    builder.addCase(getEcfEvents().rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.eventsError = message;
      state.eventsLoading = false;
    });

    // ----- EVENT DETAIL -----
    builder.addCase(getEcfEventDetails('').pending, (state) => {
      state.eventDetailLoading = true;
      state.eventDetailError = null;
    });
    builder.addCase(getEcfEventDetails('').fulfilled, (state, action) => {
      state.eventDetail = action.payload as EcfEventDetail;
      state.eventDetailLoading = false;
    });
    builder.addCase(getEcfEventDetails('').rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.eventDetailError = message;
      state.eventDetailLoading = false;
    });

    // ----- EVENT LIFECYCLE (one entity's history; separate from the ledger list) -----
    builder.addCase(getEcfEventLifecycle().pending, (state) => {
      state.lifecycleLoading = true;
      state.lifecycleError = null;
    });
    builder.addCase(getEcfEventLifecycle().fulfilled, (state, action) => {
      const { data, meta } = action.payload as EcfEventsResponse;
      state.lifecycle = data ?? [];
      state.lifecycleTotal = meta?.total ?? data?.length ?? 0;
      state.lifecycleLoading = false;
    });
    builder.addCase(getEcfEventLifecycle().rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.lifecycleError = message;
      state.lifecycleLoading = false;
    });

    // ----- LUMI CONFIG -----
    builder.addCase(getEcfLumiConfig().pending, (state, action) => {
      state.lumiConfigLoading = true;
      state.lumiConfigError = null;
      state.lumiConfigRequestId = action.meta.requestId;
      // A read that starts now sees every earlier save, so it applies in full.
      state.lumiConfigAreasSavedDuringRead = [];
    });
    builder.addCase(getEcfLumiConfig().fulfilled, (state, action) => {
      // Only the newest read writes: a superseded one landing late would revert newer state.
      if (action.meta.requestId !== state.lumiConfigRequestId) return;
      // Areas saved while it was in flight keep the saved value (and lose their stale `_errors`);
      // everything else, branding included, applies as usual.
      const saved = state.lumiConfigAreasSavedDuringRead ?? [];
      const next = withoutAreaErrors(normalizeLumiConfig(action.payload as LumiConfig), saved);
      if (state.lumiConfig) {
        applyLumiConfigRead(state.lumiConfig, next, saved);
      } else {
        state.lumiConfig = next;
      }
      state.lumiConfigAreasSavedDuringRead = [];
      state.lumiConfigLoading = false;
    });
    builder.addCase(getEcfLumiConfig().rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) {
        // Cancelled. A read superseded by a newer one leaves loading to that one; the LATEST read
        // being aborted (by a branding write, or on unmount) has nothing left to wait for.
        if (action.meta.requestId === state.lumiConfigRequestId) {
          state.lumiConfigLoading = false;
          state.lumiConfigAreasSavedDuringRead = [];
        }
        return;
      }
      // A superseded read's failure says nothing about the newest one, which still owns the state.
      if (action.meta.requestId !== state.lumiConfigRequestId) return;
      state.lumiConfigError = message;
      state.lumiConfigLoading = false;
      state.lumiConfigAreasSavedDuringRead = [];
    });

    // ----- YARDI MAPPINGS -----
    builder.addCase(getEcfYardiMappings(0).pending, (state) => {
      state.yardiMappingsLoading = true;
      state.yardiMappingsError = null;
    });
    builder.addCase(getEcfYardiMappings(0).fulfilled, (state, action) => {
      state.yardiMappings = normalizeYardiMappings(action.payload as YardiMappingsConfigResponse);
      state.yardiMappingsLoading = false;
    });
    builder.addCase(getEcfYardiMappings(0).rejected, (state, action) => {
      const message = toError(action.payload);
      if (message === null) return;
      state.yardiMappingsError = message;
      state.yardiMappingsLoading = false;
    });
  },
});

export const {
  clearEventDetail,
  setLifecycleTarget,
  setLumiConfigArea,
  setLumiBranding,
  setYardiMappings,
  clearExternalConnections,
} = externalConnectionsSlice.actions;

export default externalConnectionsSlice.reducer;
